import { lazy } from 'react';
import { CC, DIFFUSION, LIVELIHOOD, permissionKey, RESEARCH, TV } from './constants';

// Auth screens
const LoginPage = lazy(() => import('../pages/AuthScreen/Login'));
const ForgotPassword = lazy(() => import('../pages/AuthScreen/ForgotPassword'));
const VerificationCode = lazy(() => import('../pages/AuthScreen/VerificationCode'));
const ResetPassword = lazy(() => import('../pages/AuthScreen/ResetPassword'));
const PasswordToast = lazy(() => import('../pages/AuthScreen/PasswordToast'));

//Deactivate Account
const DeactivateAccount = lazy(() => import('../pages/DeactivateAccount'));
const DeactivationVerification = lazy(
  () => import('../pages/DeactivateAccount/components/Verification')
);

//Privacy Policy
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));

// Dashboard screens
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Event = lazy(() => import('../pages/Event'));
const EventView = lazy(() => import('../pages/Event/components/EventView'));
const AddEvent = lazy(() => import('../pages/Event/components/AddEvent'));
const AddParticipant = lazy(() => import('../pages/Event/components/AddParticipant'));
const Survey = lazy(() => import('../pages/Survey'));
const SurveyByID = lazy(() => import('../pages/Survey/components/SurveyByID'));
const SurveyList = lazy(() => import('../pages/Survey/components/SurveyList'));
const CreateSurvey = lazy(() => import('../pages/Survey/components/CreateSurvey'));
const SurveyResponses = lazy(() => import('../pages/Survey/components/SurveyResponses'));
const FillSurvey = lazy(() => import('../pages/Survey/components/FillSurvey'));
const FillConsent = lazy(() => import('../pages/Survey/components/FillConsent'));
const Training = lazy(() => import('../pages/Training'));
const Lessons = lazy(() => import('../pages/Training/components/LessonsList'));
const ViewLessons = lazy(() => import('../pages/Training/components/LessonsView'));
const Farmer = lazy(() => import('../pages/Farmer'));
const FarmerView = lazy(() => import('../pages/Farmer/components/FarmerView'));
const Approvals = lazy(() => import('../pages/Approvals'));
const Attendance = lazy(() => import('../pages/Attendance'));
const MarkAttendance = lazy(() => import('../pages/Attendance/MarkAttendance'));
const Requests = lazy(() => import('../pages/Requests'));
const DiffSettings = lazy(() => import('../pages/Settings/MyProfile'));

// // TV
const ScoreCard = lazy(() => import('../pages/ScoreCard'));
const Report = lazy(() => import('../pages/Reports'));
const Trace = lazy(() => import('../pages/Trace'));
const Verification = lazy(() => import('../pages/Verification'));
const FieldVerification = lazy(() => import('../pages/Verification/Field'));
const TraceAddDetails = lazy(() => import('../pages/Trace/Components/AddDetails'));
const PlantationDocumentView = lazy(
  () => import('../pages/Trace/Components/PlantationDocumentView')
);

// //CC
const MemberPortal = lazy(() => import('../pages/MemberPortal'));
const DataCollection = lazy(() => import('../pages/DataCollection'));
const AddData = lazy(() => import('../pages/DataCollection/components/AddData'));
const FarmerEntries = lazy(() => import('../pages/DataCollection/components/FarmerEntries'));
const FarmEntries = lazy(() => import('../pages/DataCollection/components/FarmEntries'));
const FarmDetails = lazy(() => import('../pages/DataCollection/components/FarmDetails'));
const WorkerDetails = lazy(() => import('../pages/DataCollection/components/WorkerDetails'));
const ChemicalDetails = lazy(() => import('../pages/DataCollection/components/ChemicalDetails'));
const ExpenseDetails = lazy(() => import('../pages/DataCollection/components/ExpenseDetails'));
const FertilizerDetails = lazy(
  () => import('../pages/DataCollection/components/FertilizerDetails')
);
const FFBDetails = lazy(() => import('../pages/DataCollection/components/FFBDetails'));
const Assessment = lazy(() => import('../pages/Assessment'));
const GroupList = lazy(() => import('../pages/Assessment/components/Grouplist'));
const GroupView = lazy(() => import('../pages/Assessment/components/GroupView'));
const HCVReport = lazy(() => import('../pages/Assessment/components/HCVReport'));
const GroupParticipant = lazy(() => import('../pages/Assessment/components/GroupParticipant'));
const ParticipantView = lazy(() => import('../pages/Assessment/components/ParticipantView'));

const RSPODisclosure = lazy(() => import('../pages/Assessment/components/RSPODisclosure'));
const GQA = lazy(() => import('../pages/Assessment/components/GQA'));
const PremiumCalculation = lazy(() => import('../pages/Assessment/components/PremiumCalculation'));
const RiskAssessment = lazy(() => import('../pages/Assessment/components/RiskAssessment'));
const Ganoderma = lazy(() => import('../pages/Assessment/components/Ganoderma'));
const AssessmentFarmerAssessment = lazy(
  () => import('../pages/Assessment/components/FarmerAssessment')
);
const AssessmentFarmImprovement = lazy(
  () => import('../pages/Assessment/components/FarmImprovement')
);
const LivelihoodFarmerAssessment = lazy(
  () => import('../pages/Livelihood/Assessment/FarmerAssessment')
);
const LivelihoodFarmImprovement = lazy(
  () => import('../pages/Livelihood/Assessment/FarmImprovement')
);
const SoilVerification = lazy(() => import('../pages/Assessment/components/SoilVerification'));
const SoilSampling = lazy(() => import('../pages/Assessment/components/SoilSampling'));
const SoilAnalysis = lazy(() => import('../pages/Assessment/components/SoilAnalysis'));
const TrunkSampling = lazy(() => import('../pages/Assessment/components/TrunkSampling'));
const TrunkAnalysis = lazy(() => import('../pages/Assessment/components/TrunkAnalysis'));
const VGM = lazy(() => import('../pages/Assessment/components/VGM'));
const GanodermaCensus = lazy(() => import('../pages/Assessment/components/GanodermaCensus'));
const FertilizerRecommendation = lazy(
  () => import('../pages/Assessment/components/FertilizerRecommendation')
);
const FertilizerReceived = lazy(() => import('../pages/Assessment/components/FertilizerReceived'));
const PlantAnalysis = lazy(() => import('../pages/Assessment/components/PlantAnalysis'));

// Potential ISH
const PotentialISH = lazy(() => import('../pages/PotentialISH'));
const ISHDocuments = lazy(() => import('../pages/PotentialISH/Components/ISHDocuments'));
const Documents = lazy(() => import('../pages/PotentialISH/Components/Documents'));
const ParticipantAgreement = lazy(
  () => import('../pages/PotentialISH/Components/ParticipantAgreement')
);
const MembershipForm = lazy(() => import('../pages/PotentialISH/Components/MembershipForm'));
const SmallHolderFill = lazy(() => import('../pages/PotentialISH/Components/SmallHolderFill'));

const AuditReport = lazy(() => import('../pages/AuditReport'));
const AuditReportGroupView = lazy(() => import('../pages/AuditReport/components/GroupView'));
const AuditInternalForm = lazy(() => import('../pages/AuditReport/components/AuditInternalForm'));

const UserManagement = lazy(() => import('../pages/UserManagement'));
const AddUser = lazy(() => import('../pages/UserManagement/components/AddUser'));
const EditFarmer = lazy(() => import('../pages/UserManagement/components/EditFarmer'));
const AddUserRole = lazy(() => import('../pages/UserManagement/components/AddUserRole'));

// Livelihood
const LivelihoodDashboard = lazy(() => import('../pages/Livelihood/Dashboard'));
const LivelihoodDataCollection = lazy(() => import('../pages/Livelihood/DataCollection'));
const LivelihoodMapping = lazy(() => import('../pages/Livelihood/Mapping'));
const LivelihoodMapping2 = lazy(() => import('../pages/Livelihood/Mapping2'));
const LivelihoodProgram = lazy(() => import('../pages/Livelihood/Program'));
const LivelihoodEvents = lazy(() => import('../pages/Livelihood/Events'));
const LivelihoodTraining = lazy(() => import('../pages/Livelihood/Training'));
const LivelihoodApproval = lazy(() => import('../pages/Livelihood/Approval'));
const LivelihoodAssessmentGroups = lazy(
  () => import('../pages/Livelihood/Assessment/AssessmentGroups')
);
const LivelihoodRISSParticipants = lazy(
  () => import('../pages/Livelihood/Assessment/RISSParticipants')
);
const LivelihoodAssessmentDetails = lazy(
  () => import('../pages/Livelihood/Assessment/AssessmentDetails')
);
const LivelihoodReportAnalyse = lazy(() => import('../pages/Livelihood/ReportAnalyse'));

// Livelihood DataCollection Components
const LivelihoodParticipantsList = lazy(
  () => import('../pages/Livelihood/DataCollection/ParticipantsList')
);
const LivelihoodFormView = lazy(() => import('../pages/Livelihood/DataCollection/FormView'));

// Livelihood Events Components
const LivelihoodEventView = lazy(() => import('../pages/Livelihood/Events/components/EventView'));
const LivelihoodAddEvent = lazy(() => import('../pages/Livelihood/Events/components/AddEvent'));
const EventAttendance = lazy(() => import('../pages/Livelihood/Events/components/EventAttendance'));

// Livelihood Training Components
const LivelihoodLessons = lazy(() => import('../pages/Livelihood/Training/components/LessonsList'));
const LivelihoodViewLessons = lazy(
  () => import('../pages/Livelihood/Training/components/LessonsView')
);

// Livelihood Farmer Journey
const LivelihoodFarmerJourney = lazy(() => import('../pages/Livelihood/FarmerJourney'));
const LivelihoodViewEventDetails = lazy(
  () => import('../pages/Livelihood/FarmerJourney/LIViewEventDetails')
);

// Livelihood Program
// //Deep Linking
const DeepLinkRedirect = lazy(() => import('../pages/DeepLinking'));

// Research pillar
const Home = lazy(() => import('../pages/Research/Home'));
const ReportsAndAnalysis = lazy(() => import('../pages/Research/ReportAnalyse'));
const ResearchDFEvents = lazy(() => import('../pages/Research/DataCollection/DF'));
const ResearchDataParticipants = lazy(
  () => import('../pages/Research/DataCollection/Common/ParticipantTable')
);
const ResearchParticipantsDetails = lazy(
  () => import('../pages/Research/DataCollection/DF/ParticipantsDetails')
);
const SurveyResult = lazy(() => import('../pages/Research/DataCollection/DF/SurveyResult'));
const TVCollections = lazy(() => import('../pages/Research/DataCollection/TV'));
const FarmerJourney = lazy(() => import('../pages/Research/FarmerJourney'));
const FarmerJourneyView = lazy(
  () => import('../pages/Research/FarmerJourney/components/FarmerView')
);
const ResearchLIViewEventDetails = lazy(
  () => import('../pages/Research/FarmerJourney/components/RELIViewEventDetails')
);
const ResearchTraining = lazy(() => import('../pages/Research/Training'));
const ResearchLessons = lazy(() => import('../pages/Research/Training/components/LessonsList'));
const ResearchViewLessons = lazy(() => import('../pages/Research/Training/components/LessonsView'));
const ResearchEvent = lazy(() => import('../pages/Research/Events'));
const ResearchAddEvent = lazy(() => import('../pages/Research/Events/components/AddEvent'));
const ResearchEventView = lazy(() => import('../pages/Research/Events/components/EventView'));
const ResearchEventAttendance = lazy(
  () => import('../pages/Research/Events/components/EventAttendance')
);
const ResearchSurvey = lazy(() => import('../pages/Research/Survey'));
const ResearchSurveyCreate = lazy(() => import('../pages/Research/Survey/components/CreateSurvey'));
const ResaerchDashboard = lazy(() => import('../pages/Research/Dashboard'));
const ResearchDiffusionDashboard = lazy(
  () => import('../pages/Research/Dashboard/DF/ResearchDFDashboard')
);
const ResearchTVDashboard = lazy(
  () => import('../pages/Research/Dashboard/TV/ResearchTVDashboard')
);
const ResearchCCDashboard = lazy(
  () => import('../pages/Research/Dashboard/CC/ResearchCCDashboard')
);
const ResearchLIDashboard = lazy(
  () => import('../pages/Research/Dashboard/LI/ResearchLIDashboard')
);

export const authRoutes = [
  { path: '/login', component: LoginPage, isAllow: true },
  { path: '/forgotPassword', component: ForgotPassword, isAllow: true },
  { path: '/verify-code', component: VerificationCode, isAllow: true },
  { path: '/reset-password', component: ResetPassword, isAllow: true },
  { path: '/password-toast', component: PasswordToast, isAllow: true },
  { path: '/redirectevent/:id', component: DeepLinkRedirect, isAllow: true },
  { path: '/deactivate-account', component: DeactivateAccount, isAllow: true },
  { path: '/deactivate-verify', component: DeactivationVerification, isAllow: true },
  { path: '/privacy-policy', component: PrivacyPolicy, isAllow: true },
];

export const dashboardRoutes = [
  {
    path: '/dashboard',
    component: Dashboard,
    pillar: DIFFUSION,
    module: 'dashboard',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/event',
    component: Event,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/event/:id',
    component: EventView,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/event/add-event',
    component: AddEvent,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.add_event,
  },
  {
    path: '/event/edit-event',
    component: AddEvent,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.edit_event,
  },
  {
    path: '/event/add-participant',
    component: AddParticipant,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.add_participants,
  },
  {
    path: '/event/edit-participant',
    component: AddParticipant,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.edit_participants,
  },
  {
    path: '/survey',
    component: Survey,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/survey/:id',
    component: SurveyByID,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/survey/list',
    component: SurveyList,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/survey/list/create-survey',
    component: CreateSurvey,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.add_survey,
  },
  {
    path: '/survey/list/edit-survey/:id',
    component: CreateSurvey,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.edit_survey,
  },
  {
    path: '/survey/list/view-survey/:id',
    component: CreateSurvey,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/survey/list/responses/:id',
    component: SurveyResponses,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/survey/fillconsent/:id',
    component: FillConsent,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.upload_survey_response,
  },
  {
    path: '/survey/fillsurvey/:id',
    component: FillSurvey,
    pillar: DIFFUSION,
    module: 'survey',
    requiredPermission: permissionKey.upload_survey_response,
  },
  {
    path: '/training',
    component: Training,
    pillar: DIFFUSION,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/training/:id',
    component: Lessons,
    pillar: DIFFUSION,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/training/lessons/:id',
    component: ViewLessons,
    pillar: DIFFUSION,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/farmer',
    component: Farmer,
    pillar: DIFFUSION,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/farmer/:id',
    component: FarmerView,
    pillar: DIFFUSION,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/approval',
    component: Approvals,
    pillar: DIFFUSION,
    module: 'approval',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/event/attendance',
    component: Attendance,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/event/attendance/mark/:id',
    component: MarkAttendance,
    pillar: DIFFUSION,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/requests',
    component: Requests,
    pillar: DIFFUSION,
    module: 'requests',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/reportAnalyse',
    component: Report,
    pillar: DIFFUSION,
    module: 'reports_and_analytics',
    requiredPermission: permissionKey.view,
  },
];

export const tvRoutes = [
  {
    path: '/tv/dashboard',
    component: Dashboard,
    pillar: TV,
    module: 'dashboard',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/scoreCard',
    component: ScoreCard,
    pillar: TV,
    module: 'scoreCard',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/tv/reportAnalyse',
    component: Report,
    pillar: TV,
    module: 'reports_and_analytics',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/setting/profile',
    component: DiffSettings,
    pillar: 'common',
    module: 'settings',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/trace',
    component: Trace,
    pillar: TV,
    module: 'trace',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/trace/add-details/:id',
    component: TraceAddDetails,
    pillar: TV,
    module: 'trace',
    requiredPermission: permissionKey.trace_data,
  },
  {
    path: '/trace/view-details/:id',
    component: TraceAddDetails,
    pillar: TV,
    module: 'trace',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/trace/edit-details/:id',
    component: TraceAddDetails,
    pillar: TV,
    module: 'trace',
    requiredPermission: permissionKey.trace_data,
  },
  {
    path: '/trace/document/:id',
    component: PlantationDocumentView,
    pillar: TV,
    module: 'trace',
    requiredPermission: permissionKey.trace_add_documents,
  },
  {
    path: '/trace/viewdocument/:id',
    component: PlantationDocumentView,
    pillar: TV,
    module: 'trace',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/tv/farmer',
    component: Farmer,
    pillar: TV,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/tv/farmer/:id',
    component: FarmerView,
    pillar: TV,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/tv/approval',
    component: Approvals,
    pillar: TV,
    module: 'approval',
    requiredPermission: permissionKey.view,
  },
  //   // { path: "/memberPortal", component: MemberPortal }
];

export const ccRoutes = [
  {
    path: '/cc/dashboard',
    component: Dashboard,
    pillar: CC,
    module: 'dashboard',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection',
    component: DataCollection,
    pillar: CC,
    module: 'datacollection',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/:id',
    component: AddData,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/verification',
    component: Verification,
    pillar: CC,
    module: 'verification',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/verification/field',
    component: FieldVerification,
    pillar: CC,
    module: 'verification',
    requiredPermission: permissionKey.edit_field,
  },
  {
    path: '/datacollection/adddata/farmerentries/:id/:farm/',
    component: FarmerEntries,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/farmentries/:id',
    component: FarmEntries,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/farmdetails/:id',
    component: FarmDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/workerdetails/:id/:farm',
    component: WorkerDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/chemicaldetails/:id/:farm/:farmID/:lot/:land',
    component: ChemicalDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/chemicaldetails/edit/:id/:farm/:chemId',
    component: ChemicalDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/fertilizerdetails/:id/:farm/:farmID/:lot/:land',
    component: FertilizerDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/fertilizerdetails/edit/:id/:farm/:fdId',
    component: FertilizerDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/expenseDetails/:id/:farm/:farmID/:lot/:land',
    component: ExpenseDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/expenseDetails/edit/:id/:farm/:expID',
    component: ExpenseDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/ffbDetails/:id/:farm/:farmID',
    component: FFBDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/datacollection/adddata/ffbDetails/edit/:id/:farm/:ffbId/:farmID',
    component: FFBDetails,
    pillar: CC,
    module: 'datacollection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH',
    component: PotentialISH,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/documents/:id',
    component: ISHDocuments,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/documentEsign/:id',
    component: Documents,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/documentEsign/participant-agreement/:id',
    component: ParticipantAgreement,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/documentEsign/membership-form/:id',
    component: MembershipForm,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/documentEsign/smallholder-fill/:id',
    component: SmallHolderFill,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/document/:id',
    component: PlantationDocumentView,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/viewdocument/:id',
    component: PlantationDocumentView,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/edit-details/:id',
    component: TraceAddDetails,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/potentialISH/view-details/:id',
    component: TraceAddDetails,
    pillar: CC,
    module: 'potential_ish',
    requiredPermission: permissionKey.view,
  },

  //li assessment report
  {
    path: '/li_report',
    component: LivelihoodRISSParticipants,
    pillar: CC,
    module: 'li_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/li_report/participant/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormsList')),
    pillar: CC,
    module: 'li_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/li_report/form/:id/:formType',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormView')),
    pillar: CC,
    module: 'li_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/li_report/gap-quantification-audit/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/GAPQuantificationAudit')),
    pillar: CC,
    module: 'li_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/li_report/farmerassessment/:id',
    component: LivelihoodFarmerAssessment,
    pillar: CC,
    module: 'li_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/li_report/farm-improvement/:id',
    component: LivelihoodFarmImprovement,
    pillar: CC,
    module: 'li_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment',
    component: Assessment,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/groupview/:id',
    component: GroupView,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/hcvreport/:id',
    component: HCVReport,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/riskassessment/:id',
    component: RiskAssessment,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/rspodisclosure/:id',
    component: RSPODisclosure,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/premiumcalculation/:id',
    component: PremiumCalculation,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/grouplist/:id',
    component: GroupList,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/groupparticipant/:id',
    component: GroupParticipant,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/groupview/:groupId/participantview/:farmerId',
    component: ParticipantView,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/gqa/:id',
    component: GQA,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/ganoderma/:id',
    component: Ganoderma,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/farmerassessment/:id',
    component: AssessmentFarmerAssessment,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/farmimprovement/:id',
    component: AssessmentFarmImprovement,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/soilverification/:id',
    component: SoilVerification,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/soil-sampling/:id',
    component: SoilSampling,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/soil-analysis/:id',
    component: SoilAnalysis,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/trunk-sampling/:id',
    component: TrunkSampling,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/trunk-analysis/:id',
    component: TrunkAnalysis,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/vgm/:id',
    component: VGM,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/ganoderma-census/:id',
    component: GanodermaCensus,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/fertilizer-recommendation/:id',
    component: FertilizerRecommendation,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/fertilizer-received/:id',
    component: FertilizerReceived,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/assessment/plant-analysis/:id',
    component: PlantAnalysis,
    pillar: CC,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/auditreport',
    component: AuditReport,
    pillar: CC,
    module: 'audit_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/auditreport/groupview/:id',
    component: AuditReportGroupView,
    pillar: CC,
    module: 'audit_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/auditreport/internal/data-collection/:id',
    component: AuditInternalForm,
    pillar: CC,
    module: 'audit_report',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/event',
    component: Event,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/event/:id',
    component: EventView,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/event/add-event',
    component: AddEvent,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.add_event,
  },
  {
    path: '/cc/event/edit-event',
    component: AddEvent,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.edit_event,
  },
  {
    path: '/cc/event/add-participant',
    component: AddParticipant,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.add_participants,
  },
  {
    path: '/cc/event/edit-participant',
    component: AddParticipant,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.edit_participants,
  },
  {
    path: '/cc/event/attendance',
    component: Attendance,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/event/attendance/mark/:id',
    component: MarkAttendance,
    pillar: CC,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/training',
    component: Training,
    pillar: CC,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/training/:id',
    component: Lessons,
    pillar: CC,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/training/lessons/:id',
    component: ViewLessons,
    pillar: CC,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/farmer',
    component: Farmer,
    pillar: CC,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/farmer/:id',
    component: FarmerView,
    pillar: CC,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/approval',
    component: Approvals,
    pillar: CC,
    module: 'approval',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/cc/reportAnalyse',
    component: Report,
    pillar: CC,
    module: 'reports_and_analytics',
    requiredPermission: permissionKey.view,
  },
];

export const superAdminRoutes = [
  { path: '/usermanagement', component: UserManagement, isAllow: true },
  { path: '/usermanagement/addUser', component: AddUser, isAllow: true },
  { path: '/usermanagement/editUser/:id', component: AddUser, isAllow: true },
  { path: '/usermanagement/editFarmer/:id', component: AddUser, isAllow: true },
  { path: '/usermanagement/addUserRole', component: AddUserRole, isAllow: true },
  { path: '/usermanagement/editUserRole/:id', component: AddUserRole, isAllow: true },
  { path: '/usermanagement/updatePassword/:id', component: EditFarmer, isAllow: true },
];

export const researchRoutes = [
  {
    path: '/research/home',
    component: Home,
    pillar: RESEARCH,
    module: 'home',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/dashboard',
    component: ResaerchDashboard,
    pillar: RESEARCH,
    module: 'dashboard',
  },
  {
    path: '/research/dashboard/diffusion',
    component: ResearchDiffusionDashboard,
    pillar: RESEARCH,
    module: 'dashboard',
    requiredPermission: 'research_df',
  },
  {
    path: '/research/dashboard/tv',
    component: ResearchTVDashboard,
    pillar: RESEARCH,
    module: 'dashboard',
    requiredPermission: 'research_tv',
  },
  {
    path: '/research/dashboard/cc',
    component: ResearchCCDashboard,
    pillar: RESEARCH,
    module: 'dashboard',
    requiredPermission: 'research_cc',
  },
  {
    path: '/research/dashboard/livelihood',
    component: ResearchLIDashboard,
    pillar: RESEARCH,
    module: 'dashboard',
    requiredPermission: 'research_li',
  },
  {
    path: '/research/event',
    component: ResearchEvent,
    pillar: RESEARCH,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/event/add-event',
    component: ResearchAddEvent,
    pillar: RESEARCH,
    module: 'events',
    requiredPermission: permissionKey.add_event,
  },
  {
    path: '/research/event/edit-event',
    component: ResearchAddEvent,
    pillar: RESEARCH,
    module: 'events',
    requiredPermission: permissionKey.edit_event,
  },
  {
    path: '/research/event/:id',
    component: ResearchEventView,
    pillar: RESEARCH,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/event/attendance/mark/:eventId',
    component: ResearchEventAttendance,
    pillar: RESEARCH,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/survey',
    component: ResearchSurvey,
    pillar: RESEARCH,
    module: 'survey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/survey/create-survey',
    component: ResearchSurveyCreate,
    pillar: RESEARCH,
    module: 'survey',
    requiredPermission: permissionKey.add_survey,
  },
  {
    path: '/research/survey/list/edit-survey/:id',
    component: ResearchSurveyCreate,
    pillar: RESEARCH,
    module: 'survey',
    requiredPermission: permissionKey.edit_survey,
  },

  // Data Collection
  {
    path: '/research/diffusionData',
    component: ResearchDFEvents,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_df',
  },
  {
    path: '/research/diffusionData/:eventId',
    component: ResearchDataParticipants,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_df',
  },
  {
    path: '/research/:pillar/participant/:id',
    component: ResearchParticipantsDetails,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_df',
  },
  {
    path: '/research/diffusionData/survey-result',
    component: SurveyResult,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_df',
  },
  {
    path: '/research/tvData',
    component: TVCollections,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_tv',
  },
  {
    path: '/research/tvData/:centerName',
    component: ResearchDataParticipants,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_tv',
  },
  {
    path: '/research/trace/view-details/:id',
    component: TraceAddDetails,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_tv',
  },
  {
    path: '/research/trace/viewdocument/:id',
    component: PlantationDocumentView,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_tv',
  },
  {
    path: '/research/ccData',
    component: ResearchDataParticipants,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_cc',
  },
  {
    path: '/research/livelihoodData',
    component: ResearchDataParticipants,
    pillar: RESEARCH,
    module: 'data_collection',
    requiredPermission: 'research_li',
  },

  {
    path: '/research/reportAnalyse',
    component: ReportsAndAnalysis,
    pillar: RESEARCH,
    module: 'reports_and_analytics',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/farmer',
    component: FarmerJourney,
    pillar: RESEARCH,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/farmer/:id',
    component: FarmerJourneyView,
    pillar: RESEARCH,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/farmer/eventDetails/:id',
    component: ResearchLIViewEventDetails,
    pillar: RESEARCH,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/training',
    component: ResearchTraining,
    pillar: RESEARCH,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/training/:id',
    component: ResearchLessons,
    pillar: RESEARCH,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/training/lessons/:id',
    component: ResearchViewLessons,
    pillar: RESEARCH,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/event/edit-participant',
    component: AddParticipant,
    pillar: RESEARCH,
    module: 'farmer_journey',
    requiredPermission: permissionKey.edit_participant_details,
  },
  {
    path: '/research/trace/view-details/:id',
    component: TraceAddDetails,
    pillar: RESEARCH,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/trace/viewdocument/:id',
    component: PlantationDocumentView,
    pillar: RESEARCH,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/research/datacollection/forms/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormsList')),
    pillar: RESEARCH,
    module: 'farmer_journey',
    byPassResearch: true,
  },
];

export const livelihoodRoutes = [
  //Dashboard Routes
  {
    path: '/livelihood/dashboard',
    component: LivelihoodDashboard,
    pillar: LIVELIHOOD,
    module: 'dashboard',
    requiredPermission: permissionKey.view,
  },

  //Mapping Routes
  {
    path: '/livelihood/mapping',
    component: LivelihoodMapping2,
    pillar: LIVELIHOOD,
    module: 'mapping',
    requiredPermission: permissionKey.view,
  },
  // { path: '/livelihood/mapping', component: LivelihoodMapping },

  // Events Routes
  {
    path: '/livelihood/event',
    component: LivelihoodEvents,
    pillar: LIVELIHOOD,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/event/add-event',
    component: LivelihoodAddEvent,
    pillar: LIVELIHOOD,
    module: 'events',
    requiredPermission: permissionKey.add_event,
  },
  {
    path: '/livelihood/event/edit-event',
    component: LivelihoodAddEvent,
    pillar: LIVELIHOOD,
    module: 'events',
    requiredPermission: permissionKey.edit_event,
  },
  {
    path: '/livelihood/event/:id',
    component: LivelihoodEventView,
    pillar: LIVELIHOOD,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/event/attendance/mark/:eventId',
    component: EventAttendance,
    pillar: LIVELIHOOD,
    module: 'events',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/event/add-participant',
    component: AddParticipant,
    pillar: LIVELIHOOD,
    module: 'events',
    requiredPermission: permissionKey.add_participants,
  },

  // Data Collection Routes
  {
    path: '/livelihood/datacollection',
    component: lazy(() => import('../pages/Livelihood/DataCollection')),
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/groups/',
    component: lazy(() => import('../pages/Livelihood/DataCollection/Groups')),
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/cc-dataset/:id',
    component: AddData,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  // {
  //   path: '/livelihood/datacollection/participants/:groupId',
  //   component: lazy(() => import('../pages/Livelihood/DataCollection/ParticipantsList')),
  //   pillar: LIVELIHOOD,
  //   module: 'data_collection',
  // },
  {
    path: '/livelihood/datacollection/forms/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormsList')),
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/gap-quantification-audit/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/GAPQuantificationAudit')),
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/form/:id/:formType',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormView')),
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  // {
  //   path: '/livelihood/datacollection/adddata/:id',
  //   component: lazy(() => import('../pages/Livelihood/DataCollection/AddData')),
  //   pillar: LIVELIHOOD,
  //   module: 'data_collection',
  //   byPassResearch: true,
  //   requiredPermission: permissionKey.view,
  // },
  {
    path: '/livelihood/datacollection/adddata/farmerentries/:id/:farm/',
    component: FarmerEntries,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/farmentries/:id',
    component: FarmEntries,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/farmdetails/:id',
    component: FarmDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/workerdetails/:id/:farm',
    component: WorkerDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/chemicaldetails/:id/:farm/:farmID/:lot/:land',
    component: ChemicalDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/chemicaldetails/edit/:id/:farm/:chemId',
    component: ChemicalDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/fertilizerdetails/:id/:farm/:farmID/:lot/:land',
    component: FertilizerDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/fertilizerdetails/edit/:id/:farm/:fdId',
    component: FertilizerDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/expenseDetails/:id/:farm/:farmID/:lot/:land',
    component: ExpenseDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/expenseDetails/edit/:id/:farm/:expID',
    component: ExpenseDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/ffbDetails/:id/:farm/:farmID',
    component: FFBDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/datacollection/adddata/ffbDetails/edit/:id/:farm/:ffbId/:farmID',
    component: FFBDetails,
    pillar: LIVELIHOOD,
    module: 'data_collection',
    byPassResearch: true,
    requiredPermission: permissionKey.view,
  },

  //Assessment Routes
  {
    path: '/livelihood/assessment',
    component: LivelihoodRISSParticipants,
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
  },
  // { path: '/livelihood/assessment/report-options/:groupId', component: LivelihoodReportOptions },
  {
    path: '/livelihood/assessment/participants/:groupId',
    component: LivelihoodRISSParticipants,
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
    byPassResearch: true,
  },
  // { path: '/livelihood/assessment/participant/:id', component: LivelihoodAssessmentDetails },
  {
    path: '/livelihood/assessment/participant/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormsList')),
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
    byPassResearch: true,
  },
  {
    path: '/livelihood/assessment/form/:id/:formType',
    component: lazy(() => import('../pages/Livelihood/DataCollection/FormView')),
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
    byPassResearch: true,
  },
  {
    path: '/livelihood/assessment/gap-quantification-audit/:id',
    component: lazy(() => import('../pages/Livelihood/DataCollection/GAPQuantificationAudit')),
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
    byPassResearch: true,
  },
  {
    path: '/livelihood/assessment/farmerassessment/:id',
    component: LivelihoodFarmerAssessment,
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
    byPassResearch: true,
  },
  {
    path: '/livelihood/assessment/farm-improvement/:id',
    component: LivelihoodFarmImprovement,
    pillar: LIVELIHOOD,
    module: 'assessment',
    requiredPermission: permissionKey.view,
    byPassResearch: true,
  },

  // Training Routes
  {
    path: '/livelihood/training',
    component: LivelihoodTraining,
    pillar: LIVELIHOOD,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/training/:id',
    component: LivelihoodLessons,
    pillar: LIVELIHOOD,
    module: 'training',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/training/lessons/:id',
    component: LivelihoodViewLessons,
    pillar: LIVELIHOOD,
    module: 'training',
    requiredPermission: permissionKey.view,
  },

  // Program Routes
  {
    path: '/livelihood/program',
    component: LivelihoodProgram,
    pillar: LIVELIHOOD,
    module: 'program',
    requiredPermission: permissionKey.view,
  },
  // { path: '/livelihood/program/participants/:id', component: LivelihoodProgramTableById },

  // Other Routes
  {
    path: '/livelihood/approval',
    component: LivelihoodApproval,
    pillar: LIVELIHOOD,
    module: 'approval',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/reportAnalyse',
    component: LivelihoodReportAnalyse,
    pillar: LIVELIHOOD,
    module: 'reports_and_analytics',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/farmer',
    component: LivelihoodFarmerJourney,
    pillar: LIVELIHOOD,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
  {
    path: '/livelihood/farmer/eventDetails/:id',
    component: LivelihoodViewEventDetails,
    pillar: LIVELIHOOD,
    module: 'farmer_journey',
    requiredPermission: permissionKey.view,
  },
];
